import { appData, tempConfigData } from "../../app/state/state.js";
import { checkDataUsage } from "./settingsApi.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    getContrastColor,
    normalizeHexColor,
} from "../../shared/ui/brandingTokens.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { createDomainOptions } from "../../shared/ui/controls/controlOptions.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";

let pendingConfigNameFocus = null;
let pendingSimpleListFocus = null;

const STREAM_SCOPE_OPTIONS_BY_KEY = Object.freeze({
    labStatus: Object.freeze([
        { value: "internal", label: "In-house" },
        { value: "external", label: "External" },
    ]),
    maintenanceStatus: Object.freeze([
        { value: "service", label: "Service" },
        { value: "issue", label: "Issue" },
    ]),
});

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""));
}

function focusPendingConfigNameInput() {
    if (!pendingConfigNameFocus) {return;}
    const {key, id} = pendingConfigNameFocus;
    pendingConfigNameFocus = null;
    const index = tempConfigData?.[key]?.findIndex((item) => item?.id === id);
    if (index === undefined || index < 0) {return;}

    const input = document.querySelector(
        `[data-key="${CSS.escape(key)}"] input[data-index="${index}"][data-prop="name"]`,
    );
    if (!input) {return;}
    input.focus();
    input.select();
}

function requestConfigNameFocus(key, id) {
    pendingConfigNameFocus = {id, key};
    requestAnimationFrame(focusPendingConfigNameInput);
}

function focusPendingSimpleListInput() {
    if (!pendingSimpleListFocus) {return;}
    const {key, index} = pendingSimpleListFocus;
    pendingSimpleListFocus = null;
    const input = document.querySelector(
        `[data-key="${CSS.escape(key)}"] input[data-index="${index}"]`,
    );
    if (!input) {return;}
    input.focus();
    input.select();
}

function requestSimpleListFocus(key, index) {
    pendingSimpleListFocus = {index, key};
    requestAnimationFrame(focusPendingSimpleListInput);
}

function renderSettingsCheckbox({
    checked = false,
    index,
    label,
    prop,
    title = "",
    value = "",
    withLabel = true,
}) {
    const checkedAttr = checked ? " checked" : "";
    const titleAttr = title ? ` title="${attrValue(title)}"` : "";
    const valueAttr = value ? ` value="${attrValue(value)}"` : "";
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} settings-boolean-choice" data-native-checkbox-label${titleAttr}>
            <input type="checkbox" data-index="${index}" data-prop="${prop}" class="${NATIVE_CHECKBOX_CLASSES}"${valueAttr}${checkedAttr}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            ${withLabel ? `<span class="text-xs text-gray-600">${label}</span>` : ""}
        </label>
    `;
}

function renderSettingsDefaultRadio({checked = false, index, name}) {
    const checkedAttr = checked ? " checked" : "";
    return `
        <label class="settings-default-choice" title="Set as default">
            <input type="radio" name="${attrValue(name)}" data-index="${index}" data-prop="default" class="settings-radio-input sr-only"${checkedAttr}>
            <span class="settings-radio-marker" aria-hidden="true">
                <span class="settings-radio-dot"></span>
            </span>
        </label>
    `;
}

function renderOrderButton({key, index, direction, disabled}) {
    const icon = direction === "up" ? "expand_less" : "expand_more";
    const label = direction === "up" ? "Move up" : "Move down";
    const disabledAttr = disabled ? " disabled" : "";
    return `
        <button type="button" class="move-config-item-btn inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)] disabled:cursor-not-allowed disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-gray-400" data-key="${attrValue(key)}" data-index="${index}" data-direction="${direction}" title="${label}" aria-label="${label}"${disabledAttr}>
            <span class="material-symbols-outlined pointer-events-none text-[18px]">${icon}</span>
        </button>
    `;
}

function renderColorControl({key, index, color}) {
    const safeColor = attrValue(color || "#cccccc");
    return `
        <span class="settings-color-control" data-link-id="color-pair-${key}-${index}">
            <input type="text" value="${safeColor}" data-index="${index}" data-prop="color" data-role="text" class="settings-color-value" placeholder="#hex">
            <input type="color" value="${safeColor}" data-index="${index}" data-prop="color" data-role="picker" class="settings-color-picker" aria-label="Pick colour">
        </span>
    `;
}

function getColorInputStyle(color) {
    const safeColor = normalizeHexColor(color, "#cccccc");
    return `background-color: ${safeColor}; color: ${getContrastColor(safeColor)};`;
}

function renderColumnHeader(label) {
    return `<span class="settings-option-column-header" aria-hidden="true">${label}</span>`;
}

function getCategoryConfigKey(statusKey) {
    switch (statusKey) {
        case "patientStatus": return "patientCategoryConfig";
        case "projectStatus": return "projectCategoryConfig";
        case "equipmentStatus": return "assetCategoryConfig";
        case "labStatus": return "labCategoryConfig";
        case "taskStatus": return "taskCategoryConfig";
        case "maintenanceStatus": return "maintenanceCategoryConfig";
        default: return "";
    }
}

function renderBooleanCell({
    checked,
    className = "",
    index,
    label,
    prop,
    title = "",
}) {
    const cellClassName = [
        "settings-option-boolean-cell",
        className,
    ].filter(Boolean).join(" ");
    return `
        <div class="${cellClassName}" data-mobile-label="${attrValue(label)}">
            ${renderSettingsCheckbox({
                checked,
                index,
                label,
                prop,
                title,
                withLabel: false,
            })}
        </div>
    `;
}

function renderStreamScopeControls(key, index, value) {
    const streamOptions = STREAM_SCOPE_OPTIONS_BY_KEY[key] || [];
    if (streamOptions.length === 0) {return "";}

    const selectedStreams = Array.isArray(value.streams) &&
        value.streams.length > 0
        ? value.streams.map(String)
        : streamOptions.map((option) => option.value);

    return `
        <div class="settings-config-flags" aria-label="Stream scope">
            ${streamOptions.map((option) => renderSettingsCheckbox({
                checked: selectedStreams.includes(option.value),
                index,
                label: option.label,
                prop: "streams",
                title: `Available for ${option.label}`,
                value: option.value,
                withLabel: true,
            })).join("")}
        </div>
    `;
}

function getSelectedStreams(key, value) {
    const streamOptions = STREAM_SCOPE_OPTIONS_BY_KEY[key] || [];
    if (streamOptions.length === 0) {return [];}
    if (Array.isArray(value?.streams) && value.streams.length > 0) {
        return value.streams.map(String);
    }
    return streamOptions.map((option) => option.value);
}

function renderMaintenanceStatusStreamHeaders() {
    return `
        ${renderColumnHeader("Service")}
        ${renderColumnHeader("Issue")}
    `;
}

function renderLabStatusStreamHeaders() {
    return `
        ${renderColumnHeader("In-house")}
        ${renderColumnHeader("External")}
    `;
}

function renderMaintenanceStatusStreamCells(key, index, value) {
    const selectedStreams = getSelectedStreams(key, value);
    const streamOptions = [
        {value: "service", label: "Service"},
        {value: "issue", label: "Issue"},
    ];

    return streamOptions.map((option) => `
        <div class="settings-option-boolean-cell settings-maintenance-status-stream-cell" data-mobile-label="${option.label}">
            ${renderSettingsCheckbox({
                checked: selectedStreams.includes(option.value),
                index,
                label: option.label,
                prop: "streams",
                title: `Available for ${option.label}`,
                value: option.value,
                withLabel: false,
            })}
        </div>
    `).join("");
}

function renderLabStatusStreamCells(key, index, value) {
    const selectedStreams = getSelectedStreams(key, value);
    const streamOptions = [
        {value: "internal", label: "In-house"},
        {value: "external", label: "External"},
    ];

    return streamOptions.map((option) => `
        <div class="settings-option-boolean-cell settings-lab-status-stream-cell" data-mobile-label="${option.label}">
            ${renderSettingsCheckbox({
                checked: selectedStreams.includes(option.value),
                index,
                label: option.label,
                prop: "streams",
                title: `Available for ${option.label}`,
                value: option.value,
                withLabel: false,
            })}
        </div>
    `).join("");
}

function getOrderedConfigEntries(key) {
    return (tempConfigData?.[key] || [])
        .map((item, index) => ({
            index,
            item,
            order: Number(item?.order ?? index + 1) || index + 1,
        }))
        .sort((a, b) => {
            const orderDiff = a.order - b.order;
            return orderDiff !== 0 ? orderDiff : a.index - b.index;
        });
}

function moveOrderedConfigItem(key, index, direction) {
    const entries = getOrderedConfigEntries(key);
    const currentPosition = entries.findIndex((entry) => entry.index === index);
    const nextPosition = direction === "up"
        ? currentPosition - 1
        : currentPosition + 1;

    if (
        currentPosition < 0 ||
        nextPosition < 0 ||
        nextPosition >= entries.length
    ) {
        return false;
    }

    entries.forEach((entry, position) => {
        if (typeof entry.item === "object" && entry.item !== null) {
            entry.item.order = position + 1;
        }
    });

    const current = entries[currentPosition].item;
    const next = entries[nextPosition].item;
    const currentOrder = current.order;
    current.order = next.order;
    next.order = currentOrder;
    return true;
}

/**
 * Binds shared native-backed behavior for status and priority settings controls.
 * @param {HTMLElement} container Rendered Settings tab container.
 * @returns {void}
 */
export function bindStatusPrioritySettingsControls(container) {
    bindNativeFieldBehavior(container);
    container.querySelectorAll(".settings-color-value").forEach((input) => {
        input.addEventListener("click", () => {
            const linkId = input.closest("[data-link-id]")?.dataset.linkId;
            const picker = linkId
                ? container.querySelector(
                    `[data-link-id="${linkId}"] [data-role="picker"]`,
                )
                : null;
            if (!picker) {return;}

            if (typeof picker.showPicker === "function") {
                try {
                    picker.showPicker();
                    return;
                } catch {
                    // Fall back to click for browsers that reject showPicker.
                }
            }
            picker.click();
        });
    });
    container.querySelectorAll(".settings-status-group-control").forEach((wrapper) => {
        const hiddenInput = wrapper.querySelector("input[type='hidden']");
        const selectRoot = wrapper.querySelector("[data-status-group-select]");
        if (!selectRoot) {return;}

        const options = createDomainOptions(tempConfigData?.[wrapper.dataset.categoryKey] || []);
        createSelectControl({
            root: selectRoot,
            options,
            value: hiddenInput?.value || "",
            clearable: true,
            searchable: options.length >= 7,
            searchThreshold: 7,
            portalMenu: true,
            placeholder: "Select a status group",
            accessibleName: "Status group",
            mirror: false,
            onChange: (value) => {
                if (!hiddenInput) {return;}
                hiddenInput.value = value;
                hiddenInput.dispatchEvent(new Event("change", { bubbles: true }));
            },
        });
    });
}

/**
 * Renders a simple ordered list editor for string-like config options.
 * @param {string} title Section title.
 * @param {string} key Config key.
 * @param {Array<*>} values Current values.
 * @returns {string} List editor HTML.
 */
export function renderSimpleListEditor(title, key, values) {
    const safeValues = Array.isArray(values) ? values : [];
    return `
        <div>
            <label class="block font-medium mb-2">${title}</label>
            <div class="settings-simple-list" data-key="${key}">
                ${safeValues.map((value, index) => `
                    <div class="settings-simple-list-row">
                        <input type="text" value="${attrValue(value)}" data-index="${index}" class="settings-simple-list-input" placeholder="Option name">
                        <button type="button" data-index="${index}" data-key="${key}" class="delete-option-btn settings-config-delete" title="Remove option" aria-label="Remove option">
                            <span class="material-symbols-outlined text-lg">delete</span>
                        </button>
                    </div>
                `).join("")}
            </div>
            <button type="button" class="add-option-btn settings-add-row-btn mt-3" data-key="${key}" data-is-color="false" data-list-kind="simple">+ Add Option</button>
        </div>
    `;
}

/**
 * Renders a status or priority option editor.
 * @param {string} title Section title.
 * @param {string} key Config key.
 * @param {Array<object>} values Current option values.
 * @param {boolean} hasColor Whether to render color controls.
 * @param {?Array<object>} [categoryConfig=null] Optional status group config.
 * @returns {string} Option editor HTML.
 */
export function renderOptionEditor(title, key, values, hasColor, categoryConfig = null) {
    const safeValues = Array.isArray(values)
        ? [...values].sort((a, b) => (a.order || 0) - (b.order || 0))
        : [];
    const showCategory = Array.isArray(categoryConfig) &&
        categoryConfig.length > 0;
    const showIsClosedToggle = [
        "patientStatus",
        "projectStatus",
        "equipmentStatus",
        "taskStatus",
        "issueStatus",
        "labStatus",
        "maintenanceStatus",
    ].includes(key);
    const isLabStatus = key === "labStatus";
    const isMaintenanceStatus = key === "maintenanceStatus";
    const isStructuredStreamStatus = isLabStatus || isMaintenanceStatus;

    return `
        <div>
            <div class="settings-option-title-row ${showIsClosedToggle ? "settings-option-title-row--flags" : ""} ${key === "equipmentStatus" ? "settings-option-title-row--issue" : ""} ${isLabStatus ? "settings-lab-status-title-row" : ""} ${isMaintenanceStatus ? "settings-maintenance-status-title-row" : ""}">
                <label class="block font-medium">${title}</label>
                ${isLabStatus ? renderLabStatusStreamHeaders() : ""}
                ${isMaintenanceStatus ? renderMaintenanceStatusStreamHeaders() : ""}
                ${renderColumnHeader("Default option")}
                ${showIsClosedToggle ? `
                    ${renderColumnHeader("Closed")}
                    ${renderColumnHeader("On hold")}
                    ${key === "equipmentStatus" ? renderColumnHeader("Issue") : ""}
                ` : ""}
                <span aria-hidden="true"></span>
            </div>
            <div class="space-y-2" data-key="${key}">
                ${safeValues.map((value, sortedIndex) => {
        const originalIndex = tempConfigData?.[key]?.findIndex(
            (item) => item && item.id === value.id,
        );
        if (originalIndex === undefined || originalIndex === -1) {return "";}
        const isFirst = sortedIndex === 0;
        const isLast = sortedIndex === safeValues.length - 1;

        return `
            <div class="settings-option-row-shell ${showIsClosedToggle ? "settings-option-row-shell--flags" : ""} ${key === "equipmentStatus" ? "settings-option-row-shell--issue" : ""} ${isLabStatus ? "settings-lab-status-row" : ""} ${isMaintenanceStatus ? "settings-maintenance-status-row" : ""}">
                <div class="settings-config-row">
                    <div class="settings-config-order-controls" aria-label="Option order controls">
                        ${renderOrderButton({key, index: originalIndex, direction: "up", disabled: isFirst})}
                        ${renderOrderButton({key, index: originalIndex, direction: "down", disabled: isLast})}
                    </div>
                    <div class="settings-config-row-main">
                        <div class="settings-config-primary">
                            <input type="text" value="${attrValue(value.name || "")}" data-index="${originalIndex}" data-prop="name" class="settings-config-name settings-config-name--colored" style="${getColorInputStyle(value.color)}" placeholder="Name">
                        </div>
                        ${hasColor ? renderColorControl({
                            key,
                            index: originalIndex,
                            color: value.color,
                        }) : ""}
                        ${showCategory ? `
                            <div class="settings-status-group-control" data-category-key="${attrValue(getCategoryConfigKey(key))}">
                                <input type="hidden" value="${attrValue(value.category || "")}" data-index="${originalIndex}" data-prop="category">
                                <div data-status-group-select></div>
                            </div>
                        ` : ""}
                        ${isStructuredStreamStatus ? "" : renderStreamScopeControls(key, originalIndex, value)}
                    </div>
                </div>
                ${isLabStatus ? renderLabStatusStreamCells(key, originalIndex, value) : ""}
                ${isMaintenanceStatus ? renderMaintenanceStatusStreamCells(key, originalIndex, value) : ""}
                <div class="settings-option-default-cell ${isLabStatus ? "settings-lab-status-default-cell" : ""} ${isMaintenanceStatus ? "settings-maintenance-status-default-cell" : ""}" data-mobile-label="Default">
                        ${renderSettingsDefaultRadio({
                            checked: value.default,
                            index: originalIndex,
                            name: `${key}-default`,
                        })}
                </div>
                ${showIsClosedToggle ? `
                    ${renderBooleanCell({
                        checked: value.isClosed,
                        className: isLabStatus ? "settings-lab-status-closed-cell" :
                            (isMaintenanceStatus ? "settings-maintenance-status-closed-cell" : ""),
                        index: originalIndex,
                        label: "Closed",
                        prop: "isClosed",
                        title: "Mark as 'Closed'",
                    })}
                    ${renderBooleanCell({
                        checked: value.isOnHold,
                        className: isLabStatus ? "settings-lab-status-on-hold-cell" :
                            (isMaintenanceStatus ? "settings-maintenance-status-on-hold-cell" : ""),
                        index: originalIndex,
                        label: "On Hold",
                        prop: "isOnHold",
                    })}
                    ${key === "equipmentStatus" ? renderBooleanCell({
                        checked: value.isIssue,
                        index: originalIndex,
                        label: "Issue",
                        prop: "isIssue",
                        title: "Status applied when issues are open",
                    }) : ""}
                ` : ""}
                <div class="settings-config-action-cell ${isLabStatus ? "settings-lab-status-action-cell" : ""} ${isMaintenanceStatus ? "settings-maintenance-status-action-cell" : ""}">
                    <button type="button" data-index="${originalIndex}" data-key="${key}" class="delete-option-btn settings-config-delete"><span class="material-symbols-outlined text-lg">delete</span></button>
                </div>
            </div>
        `;
    }).join("")}
            </div>
            <button type="button" class="add-option-btn settings-add-row-btn mt-3" data-key="${key}" data-is-color="${hasColor}">+ Add Option</button>
        </div>
    `;
}

/**
 * Renders a status group editor.
 * @param {string} title Section title.
 * @param {string} key Config key.
 * @param {Array<object>} categories Current category values.
 * @returns {string} Category editor HTML.
 */
export function renderCategoryEditor(title, key, categories) {
    const safeCategories = Array.isArray(categories)
        ? [...categories].sort((a, b) => (a.order || 0) - (b.order || 0))
        : [];
    const showIsClosedToggle = [
        "patientCategoryConfig",
        "projectCategoryConfig",
        "assetCategoryConfig",
        "labCategoryConfig",
        "taskCategoryConfig",
        "maintenanceCategoryConfig",
    ].includes(key);

    return `
        <div>
            <div class="settings-category-title-row">
                <label class="block font-medium">${title}</label>
                ${showIsClosedToggle ? `
                    ${renderColumnHeader("Closed")}
                    ${renderColumnHeader("On hold")}
                ` : ""}
                <span aria-hidden="true"></span>
            </div>
            <div class="space-y-2" data-key="${key}">
                ${safeCategories.map((category, sortedIndex) => {
        const originalIndex = tempConfigData?.[key]?.findIndex(
            (item) => item && item.id === category.id,
        );
        if (originalIndex === undefined || originalIndex === -1) {return "";}
        const isFirst = sortedIndex === 0;
        const isLast = sortedIndex === safeCategories.length - 1;

        return `
            <div class="settings-category-row-shell">
                <div class="settings-config-row">
                    <div class="settings-config-order-controls" aria-label="Group order controls">
                        ${renderOrderButton({key, index: originalIndex, direction: "up", disabled: isFirst})}
                        ${renderOrderButton({key, index: originalIndex, direction: "down", disabled: isLast})}
                    </div>
                    <div class="settings-config-row-main">
                        <div class="settings-config-primary">
                            <input type="text" value="${attrValue(category.name || "")}" data-index="${originalIndex}" data-prop="name" class="settings-config-name settings-config-name--colored" style="${getColorInputStyle(category.color)}" placeholder="Group Name">
                        </div>
                        ${renderColorControl({
                            key,
                            index: originalIndex,
                            color: category.color,
                        })}
                    </div>
                </div>
                ${showIsClosedToggle ? `
                    ${renderBooleanCell({
                        checked: category.isClosed,
                        index: originalIndex,
                        label: "Closed",
                        prop: "isClosed",
                    })}
                    ${renderBooleanCell({
                        checked: category.isOnHold,
                        index: originalIndex,
                        label: "On Hold",
                        prop: "isOnHold",
                    })}
                ` : ""}
                <div class="settings-config-action-cell">
                    <button type="button" data-index="${originalIndex}" data-key="${key}" class="delete-category-btn settings-config-delete"><span class="material-symbols-outlined text-lg">delete</span></button>
                </div>
            </div>
        `;
    }).join("")}
            </div>
            <button type="button" class="add-category-btn settings-add-row-btn mt-3" data-key="${key}">+ Add Group</button>
        </div>
    `;
}


/**
 * Handles status, priority, category, and simple-list option buttons.
 * @param {HTMLElement} target Event target.
 * @param {object} options Handler dependencies.
 * @param {Function} options.updateSaveButton Dirty-state callback.
 * @param {Function} options.rerenderCurrentTabContent Rerender callback.
 * @returns {Promise<boolean>} True when the click was handled.
 */
export async function handleStatusPrioritySettingsButtonClick(target, options = {}) {
    const {
        updateSaveButton = () => {},
        rerenderCurrentTabContent = () => {},
    } = options;

    if (target.closest(".move-config-item-btn")) {
        const btn = target.closest(".move-config-item-btn");
        const key = btn.dataset.key;
        const index = parseInt(btn.dataset.index, 10);
        const direction = btn.dataset.direction;

        if (moveOrderedConfigItem(key, index, direction)) {
            updateSaveButton(true);
            rerenderCurrentTabContent();
        }
        return true;
    }

    if (target.closest(".add-category-btn")) {
        const key = target.closest(".add-category-btn").dataset.key;
        if (!tempConfigData[key]) {tempConfigData[key] = [];}

        const maxOrder = tempConfigData[key].length > 0
            ? Math.max(0, ...tempConfigData[key].map((cat) => cat.order || 0))
            : 0;
        const newCategory = {
            id: `cat_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            name: "New Group",
            color: "#cccccc",
            order: maxOrder + 1,
            isClosed: false,
            isOnHold: false,
        };
        tempConfigData[key].push(newCategory);

        updateSaveButton(true);
        rerenderCurrentTabContent();
        requestConfigNameFocus(key, newCategory.id);
        return true;
    }

    if (target.closest(".delete-category-btn")) {
        const btn = target.closest(".delete-category-btn");
        const key = btn.dataset.key;
        const index = parseInt(btn.dataset.index, 10);

        openConfirmationModal(
            "Delete Group?",
            "Are you sure you want to delete this group? Items using this group will be unassigned.",
            () => {
                tempConfigData[key].splice(index, 1);
                updateSaveButton(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    if (target.closest(".add-option-btn")) {
        const btn = target.closest(".add-option-btn");
        const key = btn.dataset.key;
        const isColor = btn.dataset.isColor === "true";
        if (!tempConfigData[key]) {tempConfigData[key] = [];}

        if (btn.dataset.listKind === "simple") {
            const nextIndex = tempConfigData[key].length;
            tempConfigData[key].push("New Option");
            updateSaveButton(true);
            rerenderCurrentTabContent();
            requestSimpleListFocus(key, nextIndex);
            return true;
        }

        let defaultCategory = null;
        if (
            key === "patientStatus" ||
            key === "labStatus" ||
            key === "maintenanceStatus"
        ) {
            defaultCategory = tempConfigData?.[getCategoryConfigKey(key)]?.[0]?.id ||
                null;
        }

        const maxOrder = tempConfigData[key].length > 0
            ? Math.max(0, ...tempConfigData[key].map((item) => item.order || 0))
            : 0;

        const newItem = {
            id: `opt_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
            name: "New Option",
            order: maxOrder + 1,
            default: tempConfigData[key].length === 0,
        };
        if (isColor) {newItem.color = "#cccccc";}
        if (defaultCategory) {newItem.category = defaultCategory;}
        if (
            [
                "patientStatus",
                "projectStatus",
                "equipmentStatus",
                "taskStatus",
                "issueStatus",
                "labStatus",
                "maintenanceStatus",
            ].includes(key)
        ) {
            newItem.isClosed = false;
            newItem.isOnHold = false;
        }

        tempConfigData[key].push(newItem);
        updateSaveButton(true);
        rerenderCurrentTabContent();
        requestConfigNameFocus(key, newItem.id);
        return true;
    }

    if (target.closest(".delete-option-btn")) {
        const btn = target.closest(".delete-option-btn");
        const key = btn.dataset.key;
        const index = parseInt(btn.dataset.index, 10);
        const optionToDelete = tempConfigData[key][index];
        if (!optionToDelete || typeof optionToDelete !== "object") {
            openConfirmationModal(
                "Delete Option?",
                "Are you sure you want to delete this option? This cannot be undone.",
                () => {
                    tempConfigData[key].splice(index, 1);
                    updateSaveButton(true);
                    rerenderCurrentTabContent();
                },
            );
            return true;
        }
        const idToDelete = optionToDelete.id;

        const originalIcon = btn.innerHTML;
        btn.innerHTML = '<span class="material-symbols-outlined text-lg animate-spin">progress_activity</span>';
        btn.disabled = true;

        let checkType = null;
        let typeLabel = "items";

        if (key === "taskStatus") {
            checkType = "task";
            typeLabel = "Tasks";
        } else if (key === "patientStatus") {
            checkType = "patient";
            typeLabel = "Patients";
        } else if (key === "projectStatus") {
            checkType = "project";
            typeLabel = "Projects";
        } else if (key === "equipmentStatus") {
            checkType = "asset";
            typeLabel = "Assets";
        } else if (key === "labStatus") {
            checkType = "lab";
            typeLabel = "Labs";
        } else if (key === "maintenanceStatus") {
            checkType = "maintenance";
            typeLabel = "Maintenance";
        } else if (key === "priorities") {
            checkType = "priorities";
            typeLabel = "items (Global)";
        }

        let usageCount = 0;
        if (checkType) {
            usageCount = await checkDataUsage(checkType, idToDelete);
        } else if (key === "issueStatus") {
            usageCount = (appData.assets || []).reduce((total, asset) => {
                const logs = asset.issue_log || [];
                return total + logs.filter((log) => log.status === idToDelete).length;
            }, 0);
            typeLabel = "Asset Issues (visible only)";
        }

        btn.innerHTML = originalIcon;
        btn.disabled = false;

        if (usageCount > 0) {
            openConfirmationModal(
                "Cannot Delete Status",
                `This option is currently used by <strong>${usageCount} ${typeLabel}</strong> in the database.<br><br>You must migrate these items to a different status before you can delete this option.`,
                () => {},
                false,
                "Okay",
            );
            return true;
        }

        if (usageCount === -1) {
            openConfirmationModal(
                "Error",
                "Could not verify usage on server. Please try again or check your connection.",
                () => {},
                false,
                "OK",
            );
            return true;
        }

        openConfirmationModal(
            "Delete Option?",
            "Are you sure you want to delete this option? This cannot be undone.",
            () => {
                tempConfigData[key].splice(index, 1);
                updateSaveButton(true);
                rerenderCurrentTabContent();
            },
        );
        return true;
    }

    return false;
}
